import {
  forwardRef,
  useRef,
  useState,
} from "react";
import FloatingLabelInput from "../../employer/details/New_Ui/Component-UI/FloatPlaceholderInputField";
import Input from "../old-ui/Input";
import { cn } from "../../../util/cn";
import EmojiPickerPopover from "./EmojiPickerPopover";
import EmojiTriggerIcon from "./EmojiTriggerIcon";
import { insertEmojiAtCursor, notifyFieldChange, toFieldString } from "./insertEmojiAtCursor";
import { useEmojiPickerDismiss } from "./useEmojiPickerDismiss";
import "./emojiFlagPolyfill.css";

type EmojiInputVariant = "floating" | "admin" | "simple";

interface EmojiInputProps {
  name: string;
  display?: string;
  placeholder?: string;
  readOnly?: boolean;
  maxLength?: number;
  required?: boolean;
  error?: string;
  touched?: boolean;
  value?: string;
  onChange?: (event: any) => void;
  onBlur?: () => void;
  variant?: EmojiInputVariant;
  className?: string;
  showInfo?: boolean;
  showInfoText?: string;
}

const EmojiInput = forwardRef(
  (
    {
      name,
      display = "",
      placeholder,
      readOnly = false,
      maxLength,
      required = false,
      error,
      touched,
      value = "",
      onChange,
      onBlur,
      variant = "floating",
      className,
      showInfo,
      showInfoText,
    }: EmojiInputProps,
    ref: any,
  ) => {
    const [showPicker, setShowPicker] = useState(false);
    const inputRef = useRef(null);
    const triggerRef = useRef(null);
    const pickerRef = useRef(null);

    useEmojiPickerDismiss(showPicker, () => setShowPicker(false), triggerRef, pickerRef);

    const setRefs = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const emitChange = (nextValue: string) => {
      notifyFieldChange(onChange, nextValue, {
        name,
        asEvent: variant === "simple",
      });
    };

    const handleEmojiSelect = (emoji: string) => {
      const input = inputRef.current as HTMLInputElement | null;
      const { value: nextValue, cursorPosition } = insertEmojiAtCursor(
        toFieldString(value),
        emoji,
        input?.selectionStart ?? null,
        input?.selectionEnd ?? null,
      );

      emitChange(nextValue);
      setShowPicker(false);

      requestAnimationFrame(() => {
        if (input) {
          input.focus();
          input.setSelectionRange(cursorPosition, cursorPosition);
        }
      });
    };

    const sharedInputProps = {
      name,
      display,
      placeholder,
      readOnly,
      maxLength,
      required,
      error,
      touched,
      value: toFieldString(value),
      onChange,
      onBlur,
      ref: setRefs,
      showInfo,
      showInfoText,
    };

    return (
      <div className={cn("relative emoji-flag-polyfill", className)}>
        {variant === "floating" && <FloatingLabelInput {...sharedInputProps} />}
        {variant === "admin" && <Input {...sharedInputProps} />}
        {variant === "simple" && (
          <input
            ref={setRefs}
            className="w-full rounded border border-gray-300 bg-white px-3 py-1 leading-8 outline-none focus:border-blue-500 focus:ring-1"
            name={name}
            type="text"
            value={toFieldString(value)}
            placeholder={placeholder}
            readOnly={readOnly}
            maxLength={maxLength}
            onChange={(event) => emitChange(event.target.value)}
            onBlur={onBlur}
          />
        )}

        {!readOnly && (
          <>
            <button
              ref={triggerRef}
              type="button"
              className={cn(
                "absolute z-10 flex items-center justify-center text-gray-500 hover:text-gray-700",
                variant === "floating" ? "right-2 top-6" : "right-3 top-3",
                variant === "simple" && "top-2",
              )}
              onClick={() => setShowPicker((open: boolean) => !open)}
              aria-label="Insert emoji"
              aria-expanded={showPicker}
            >
              <EmojiTriggerIcon />
            </button>
            {showPicker && (
              <EmojiPickerPopover
                anchorEl={triggerRef.current}
                onSelect={handleEmojiSelect}
                onClose={() => setShowPicker(false)}
                pickerRef={(node) => {
                  pickerRef.current = node;
                }}
              />
            )}
          </>
        )}
      </div>
    );
  },
);

EmojiInput.displayName = "EmojiInput";

export default EmojiInput;
